import type { ReactNode } from "react";
import type { ProductArrivalDTO, ProductFocusDTO, ProductSheetDTO, StockStatusKey } from "@easyshop/shared";
import { Drawer, ErrorState, Loading, StockBadge, cx } from "@/components/ui";
import { useSession } from "@/hooks/useSession";
import { useUi } from "@/hooks/useUi";
import { useProductSheet } from "@/lib/queries";
import { dateFR, eur, eur2, margin, num, relative } from "@/lib/format";

/** Fiche produit (panneau droit) ouverte depuis les tops, le stock, etc. */
export function ProductSheetDrawer() {
  const { product, closeProduct } = useUi();
  const focus = product?.scope === "boutique" ? product.boutiqueId : undefined;
  const q = useProductSheet(product, focus);
  return (
    <Drawer open={Boolean(product)} onClose={closeProduct} width={480}>
      <div className="flex items-start gap-[10px]">
        <div className="min-w-0 flex-1">
          <div className="font-display text-[18px] font-bold leading-tight">{product?.name}</div>
          {q.data && (q.data.category || q.data.subCategory) && (
            <div className="mt-1 text-[12px] text-muted-2">
              {[q.data.category, q.data.subCategory].filter(Boolean).join(" › ")}
            </div>
          )}
        </div>
        <button type="button" aria-label="Fermer" onClick={closeProduct} className="text-[18px] text-muted-2">
          ×
        </button>
      </div>
      {q.isLoading ? (
        <Loading />
      ) : q.error ? (
        <ErrorState error={q.error} />
      ) : q.data && product ? (
        <SheetBody sheet={q.data} />
      ) : null}
    </Drawer>
  );
}

function SheetBody({ sheet }: { sheet: ProductSheetDTO }) {
  const { isManagerView, me } = useSession();
  const scopeLabel = isManagerView ? "mes boutiques" : "réseau";
  const f = sheet.focus;
  const multi = me.boutiques.length > 1;

  // Focus boutique : les chiffres de la boutique ; sinon ceux du périmètre.
  const v = f
    ? {
        price: f.price,
        supplyPrice: f.supplyPrice,
        unitMargin: f.unitMargin,
        marginPct: f.marginPct,
        stock: f.stock,
        sold: f.soldWindow,
        coverage: f.coverageDays,
        costValue: f.stockCostValue,
        retailValue: f.stockRetailValue,
        lastRestockAt: f.lastRestockAt,
        lastSoldAt: f.lastSoldAt,
        status: f.status,
        promo: f.promo,
      }
    : {
        price: sheet.price,
        supplyPrice: sheet.supplyPrice,
        unitMargin: sheet.unitMargin,
        marginPct: sheet.marginPct,
        stock: sheet.stockTotal,
        sold: sheet.soldTotal,
        coverage: sheet.coverageDays,
        costValue: sheet.stockCostValue,
        retailValue: sheet.stockRetailValue,
        lastRestockAt: sheet.lastRestockAt,
        lastSoldAt: sheet.lastSoldAt,
        status: sheet.status,
        promo: sheet.promo,
      };
  const lastArrival = sheet.arrivals.find((a) => !f || a.boutiqueId === f.boutiqueId) ?? null;

  return (
    <div className="flex flex-col gap-[18px]">
      <div className="flex flex-wrap items-center gap-2">
        {v.status && <StockBadge status={v.status as StockStatusKey} className="px-[10px] text-[11px]" />}
        {v.promo && (
          <span className="whitespace-nowrap rounded-full bg-accent px-[10px] py-[3px] text-[11px] font-bold text-white">{v.promo.label}</span>
        )}
        {f && !f.found && <span className="text-[12px] font-semibold text-warn">Produit absent du catalogue de {f.city}</span>}
      </div>

      {/* Identité : prix, marge */}
      <div className="grid grid-cols-2 gap-[10px]">
        <Tile label="Prix de vente TTC" value={eur2(v.price)}>
          {!f && sheet.priceRange && (
            <div className="mt-1 text-[11px] text-warn">
              de {eur2(sheet.priceRange.min)} à {eur2(sheet.priceRange.max)} selon la boutique
            </div>
          )}
        </Tile>
        <Tile label="Prix d'achat" value={eur2(v.supplyPrice)}>
          {v.supplyPrice == null && <div className="mt-1 text-[11px] text-faint">non renseigné dans Hiboutik</div>}
        </Tile>
        <Tile label="Marge unitaire" value={eur2(v.unitMargin)} valueClassName={v.unitMargin != null && v.unitMargin < 0 ? "text-danger" : undefined} />
        <Tile label="Taux de marge" value={margin(v.marginPct)} />
      </div>

      {/* Stock & rotation */}
      <Section title={f ? `Stock — ${f.city}` : `Stock (${scopeLabel})`}>
        <div className="grid grid-cols-3 gap-[10px]">
          <Tile label="En stock" value={v.stock == null ? "—" : num(v.stock)} small />
          <Tile label={`Vendus (${sheet.windowDays} j)`} value={num(v.sold)} small />
          <Tile label="Couverture" value={v.coverage == null ? "—" : `${num(v.coverage)} j`} small />
          <Tile label="Valeur (achat)" value={eur(v.costValue)} small />
          <Tile label="Valeur (vente)" value={eur(v.retailValue)} small />
          <Tile label="Dernière vente" value={v.lastSoldAt ? relative(v.lastSoldAt) : "—"} small />
        </div>
      </Section>

      {/* Réassort */}
      <Section title="Dernier réassort">
        {lastArrival ? (
          <div className="rounded-card-sm border border-line px-4 py-3">
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-[13.5px] font-bold">{dateFR(lastArrival.date)}</span>
              <span className="text-[13px] font-bold tabular">+{num(lastArrival.quantity)} u.</span>
            </div>
            <div className="mt-[2px] text-[12px] text-muted-2">
              {lastArrival.supplier ?? "Sans fournisseur"}
              {multi && !f ? ` · ${lastArrival.city}` : ""}
              {lastArrival.unitCost != null ? ` · ${eur2(lastArrival.unitCost)} / u.` : ""}
            </div>
          </div>
        ) : v.lastRestockAt ? (
          <div className="text-[12.5px]">
            Réassort constaté le <b>{dateFR(v.lastRestockAt)}</b> (hors arrivage Hiboutik).
          </div>
        ) : (
          <div className="text-[12.5px] text-muted">Aucun arrivage enregistré pour ce produit.</div>
        )}
        {sheet.arrivals.length > 1 && <ArrivalsHistory arrivals={sheet.arrivals.filter((a) => a !== lastArrival)} showCity={multi} />}
      </Section>

      {/* Répartition dans le périmètre */}
      {multi && <NetworkBreakdown sheet={sheet} scopeLabel={scopeLabel} focus={f} />}
    </div>
  );
}

function ArrivalsHistory({ arrivals, showCity }: { arrivals: ProductArrivalDTO[]; showCity: boolean }) {
  return (
    <div className="mt-2">
      <div className="mb-1 text-[11px] font-bold uppercase text-muted-2">Arrivages précédents</div>
      {arrivals.map((a, i) => (
        <div key={i} className="flex justify-between gap-3 border-t border-line-soft py-[7px] text-[12px] first:border-t-0">
          <span className="min-w-0 truncate text-muted">
            {dateFR(a.date)} · {a.supplier ?? "Sans fournisseur"}
            {showCity ? ` · ${a.city}` : ""}
          </span>
          <span className="flex-shrink-0 font-semibold tabular">
            +{num(a.quantity)} u.{a.unitCost != null ? ` · ${eur2(a.unitCost)}` : ""}
          </span>
        </div>
      ))}
    </div>
  );
}

function NetworkBreakdown({ sheet, scopeLabel, focus }: { sheet: ProductSheetDTO; scopeLabel: string; focus: ProductFocusDTO | null }) {
  const maxStock = Math.max(1, ...sheet.stockByBoutique.map((s) => s.stock ?? 0));
  const topSales = [...sheet.salesByBoutique].sort((a, b) => b.quantity - a.quantity).slice(0, 8);
  const maxSales = Math.max(1, ...topSales.map((s) => s.quantity));
  return (
    <>
      {focus && (
        <div className="flex flex-wrap gap-[10px]">
          <Tile label={`Stock (${scopeLabel})`} value={num(sheet.stockTotal)} small />
          <Tile label={`Ventes (${scopeLabel})`} value={num(sheet.soldTotal)} small />
        </div>
      )}
      {!focus && (
        <Tile label="Boutiques en rupture (≤ 2)" value={num(sheet.boutiquesRupture)} valueClassName={sheet.boutiquesRupture ? "text-danger" : undefined} small />
      )}
      <Section title={`Stock par boutique (${scopeLabel})`}>
        <div className="flex max-h-[220px] flex-col gap-2 overflow-auto">
          {sheet.stockByBoutique.map((s) => (
            <BarRow
              key={s.boutiqueId}
              label={s.city}
              pct={((s.stock ?? 0) / maxStock) * 100}
              color={s.type === "INTEGRATED" ? "bg-positive" : "bg-franchise"}
              value={s.stock == null ? "—" : num(s.stock)}
              strong={s.boutiqueId === focus?.boutiqueId}
            />
          ))}
        </div>
        {sheet.boutiquesRupture > 0 && (
          <div className="mt-[10px] text-[11.5px] text-warn">Suggestion : transférer du stock des boutiques en surstock vers celles en rupture.</div>
        )}
      </Section>
      <Section title={`Ventes par boutique (${sheet.windowDays} j, top 8)`}>
        <div className="flex flex-col gap-2">
          {topSales.every((s) => s.quantity === 0) && <div className="text-[12px] text-muted">Aucune vente sur la période.</div>}
          {topSales
            .filter((s) => s.quantity > 0)
            .map((s) => (
              <BarRow
                key={s.boutiqueId}
                label={s.city}
                pct={(s.quantity / maxSales) * 100}
                color="bg-product-bar"
                value={num(s.quantity)}
                strong={s.boutiqueId === focus?.boutiqueId}
              />
            ))}
        </div>
      </Section>
    </>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <div className="mb-[10px] text-[12.5px] font-bold">{title}</div>
      {children}
    </div>
  );
}

function Tile({
  label,
  value,
  valueClassName,
  small = false,
  children,
}: {
  label: string;
  value: ReactNode;
  valueClassName?: string;
  small?: boolean;
  children?: ReactNode;
}) {
  return (
    <div className="min-w-0 flex-1 rounded-card-sm bg-page px-4 py-3">
      <div className="truncate text-[11px] font-semibold text-muted-2">{label}</div>
      <div className={cx("mt-1 font-display font-extrabold tabular", small ? "text-[16px]" : "text-[20px]", valueClassName)}>{value}</div>
      {children}
    </div>
  );
}

function BarRow({ label, pct, color, value, strong }: { label: string; pct: number; color: string; value: string; strong?: boolean }) {
  return (
    <div className="flex items-center gap-2">
      <span className={cx("w-[100px] flex-shrink-0 truncate text-[11px]", strong && "font-bold")}>{label}</span>
      <div className="h-[6px] flex-1 overflow-hidden rounded-[3px] bg-track">
        <div className={cx("h-full", color)} style={{ width: `${Math.max(0, Math.min(100, pct))}%` }} />
      </div>
      <span className="w-[32px] text-right text-[11px] font-bold tabular">{value}</span>
    </div>
  );
}
